import React, { useState, useEffect } from "react";
import { useParams,useNavigate } from "react-router-dom";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import API_URL from "../../api/API";
import { StaffSideMenu } from "../staff/StaffSideMenu";

export const AdminAthleteCreateDynamicSpeedWorkout = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [dates, setDates] = useState([{ date: "", themes: [{ title: "", sprints: [] }] }]);
  const [athleteName, setAthleteName] = useState("");
  const [existingWorkout, setExistingWorkout] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);


  // State to trigger refetch after a workout is saved
  const [isWorkoutSaved, setIsWorkoutSaved] = useState(false);


  const fetchAthleteData = async () => {
    try {
      const response = await fetch(`${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`, {
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch athlete details");
      const athleteData = await response.json();
      setAthleteName(athleteData.firstname + " " + athleteData.lastname);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExistingWorkout = async () => {
    try {
      const response = await fetch(`${API_URL.GET_ATHLETE_DYNAMIC_WORKOUT}${athlete_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${staff.token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch athlete's workout data");
      const workoutData = await response.json();
      setExistingWorkout(workoutData.workout.dates);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchAthleteData();
    fetchExistingWorkout();
  }, [athlete_id, staff.token]);

    // Trigger refetch after saving a workout
    useEffect(() => {
      if (isWorkoutSaved) {
        fetchExistingWorkout();
        setIsWorkoutSaved(false); // Reset after fetching
      }
    }, [isWorkoutSaved]); // Refetch after saving

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // Months are 0-indexed
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleAddSprint = (dateIndex, themeIndex) => {
    const updatedDates = [...dates];
    updatedDates[dateIndex].themes[themeIndex].sprints.push({
      sets: 0,
      distance: 0,
      reps: 0,
      rest: "",
      target_time: 0,
      notes: "",
      video_url: "",
    });
    setDates(updatedDates);
  };

  const handleInputChange = (e, field, dateIndex, themeIndex, sprintIndex) => {
    const updatedDates = [...dates];
    if (sprintIndex !== undefined) {
      updatedDates[dateIndex].themes[themeIndex].sprints[sprintIndex][field] = e.target.value;
    } else if (themeIndex !== undefined) {
      updatedDates[dateIndex].themes[themeIndex][field] = e.target.value;
    } else {
      updatedDates[dateIndex][field] = e.target.value;
    }
    setDates(updatedDates);
  };

  const handleDeleteSprint = (dateIndex, themeIndex, sprintIndex) => {
    const updatedDates = [...dates];
    updatedDates[dateIndex].themes[themeIndex].sprints = updatedDates[dateIndex].themes[
      themeIndex
    ].sprints.filter((_, index) => index !== sprintIndex);
    setDates(updatedDates);
  };

  const validateForm = () => {
    // Validate dates
    for (const dateEntry of dates) {
      if (!dateEntry.date) return "Date is required";
      
      // Validate themes and sprints
      for (const theme of dateEntry.themes) {
        if (!theme.title) return "Theme title is required";
        for (const sprint of theme.sprints) {
          if (sprint.sets <= 0) return "Sets must be greater than 0";
          if (sprint.distance <= 0) return "Distance must be greater than 0";
          if (sprint.reps <= 0) return "Reps must be greater than 0";
          if (sprint.target_time <= 0) return "Target time must be greater than 0";
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(API_URL.POST_SAVE_ATHLETE_DYNAMIC_WORKOUT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ athlete_id, dates }),
      });

      if (!response.ok) {
        setError("Failed to save workout");
      } else {
        alert("Workout saved successfully!");
        setDates([{ date: "", themes: [{ title: "", sprints: [] }] }]);
        setError(""); // Clear the error state upon successful save
        setIsWorkoutSaved(true); // Mark that the workout was saved
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };


    // Handle Edit (redirect to editing page)
    const handleEditTheme = (theme_id) => {
        navigate(`/administrator/athlete/speed-workout/edit/${athlete_id}/${theme_id}`);
      };
    
    const handleDeleteTheme = async (theme_id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this theme?");
        if (!confirmDelete) return;

        try {
          const response = await fetch(`${API_URL.DELETE_ATHLETE_WORKOUT_BY_THEME}${athlete_id}/${theme_id}`, {
            method: "POST",
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${staff.token}`,
            },
          });

          if (!response.ok) {
            throw new Error("Failed to delete the theme");
          }

          alert("Theme deleted successfully.");
          setIsWorkoutSaved(true);
        } catch (err) {
          setError(err.message);
        }
      };

  return (
    <div className="flex h-screen">
      <StaffSideMenu />
      <div className="flex-grow flex p-6">
      {/* Left side: Manage Athlete Dynamic Workout */}
{existingWorkout.length === 0 ? (
  <p className="text-center text-gray-600 mt-4">No workout data available for this athlete.</p>
) : (
  <div className="w-1/2 p-6 overflow-y-auto bg-gray-50 rounded-lg shadow-md">
    <h2 className="text-2xl font-semibold mb-6 text-gray-800">Manage Athlete Dynamic Speed Workout</h2>

    {existingWorkout.map((dateEntry, dateIndex) => (
      <div key={dateIndex} className="border p-6 my-6 rounded-lg shadow-lg bg-white">
        <div className="mb-6">
          <strong className="text-xl font-semibold text-indigo-600">{formatDate(dateEntry.date)}</strong>
        </div>

        {dateEntry.themes.map((theme, themeIndex) => {
          // Compute the total distance for the theme
          const totalDistance = theme.sprints.reduce((acc, sprint) => acc + sprint.distance, 0);

          return (
            <div key={themeIndex} className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-lg font-medium text-gray-800 flex items-center">
                  <span>{theme.title}</span>
                  <span className="ml-2 text-sm text-green-600">(Total Distance: {totalDistance} meters)</span>
                </div>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleEditTheme(theme._id)}
                    className="text-blue-500 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTheme(theme._id)}
                    className="text-red-500 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {theme.sprints.map((sprint, sprintIndex) => (
                <div key={sprintIndex} className="text-gray-700 mt-2">
                  <span>Sets: {sprint.sets}</span> | <span>Distance: {sprint.distance} meters</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    ))}
  </div>
)}

          <div className="w-1/2 p-4">
            {/* Go Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="absolute top-1 right-4 bg-red-500 text-white py-2 px-4 rounded mb-4"
            >
              Go Back
            </button>

            {/* Display Loading or Error message */}
            {isLoading && (
              <div className="bg-yellow-100 text-yellow-700 p-4 mb-4 rounded">
                <p>Loading...</p>
              </div>
            )}
            {error && (
              <div className="bg-red-100 text-red-700 p-4 mb-4 rounded">
                <p>Error: {error}</p>
              </div>
            )}

            <h1 className="text-2xl font-bold">Dynamic Speed Workout: <span className="text-green-600">{athleteName}</span></h1>

            {dates.map((dateEntry, dateIndex) => (
              <div key={dateIndex} className="p-4 my-4 rounded-lg shadow-lg">
                <div className="mb-4">
                  <label className="block font-semibold">Date</label>
                  <input
                    type="date"
                    value={dateEntry.date}
                    className="border p-2 w-full"
                    onChange={(e) => handleInputChange(e, "date", dateIndex)}
                  />
                </div>
                {dateEntry.themes.map((theme, themeIndex) => (
                  <div key={themeIndex} className="p-4 my-2 rounded-lg shadow-md">
                    <div className="mb-4">
                      <label className="block font-semibold">Theme Title</label>
                      <input
                        type="text"
                        value={theme.title}
                        className="border p-2 w-full"
                        onChange={(e) => handleInputChange(e, "title", dateIndex, themeIndex)}
                      />
                    </div>
                    {theme.sprints.map((sprint, sprintIndex) => (
                      <div key={sprintIndex} className="p-4 my-2 rounded-md">
                        <div className="flex space-x-4">
                          {[
                            { label: "Sets", field: "sets", type: "number" },
                            { label: "Distance", field: "distance", type: "number" },
                            { label: "Reps", field: "reps", type: "number" },
                            { label: "Target Time", field: "target_time", type: "number" },
                          ].map(({ label, field, type }) => (
                            <div className="flex-1 mb-4" key={field}>
                              <label className="block font-semibold">{label}</label>
                              <input
                                type={type}
                                value={sprint[field]}
                                className="border p-2 w-full"
                                onChange={(e) =>
                                  handleInputChange(e, field, dateIndex, themeIndex, sprintIndex)
                                }
                              />
                            </div>
                          ))}
                        </div>
                        {[
                          { label: "Rest", field: "rest", type: "text" },
                          { label: "Notes", field: "notes", type: "text" },
                        ].map(({ label, field, type }) => (
                          <div className="mb-4" key={field}>
                            <label className="block font-semibold">{label}</label>
                            <textarea
                              value={sprint[field]}
                              className="border p-2 w-full"
                              onChange={(e) =>
                                handleInputChange(e, field, dateIndex, themeIndex, sprintIndex)
                              }
                            />
                          </div>
                        ))}
                        <div className="mb-4">
                          <label className="block font-semibold">Video URL</label>
                          <input
                            type="text"
                            value={sprint.video_url}
                            className="border p-2 w-full"
                            onChange={(e) =>
                              handleInputChange(e, "video_url", dateIndex, themeIndex, sprintIndex)
                            }
                          />
                        </div>
                        <div className="text-right">
                          <button
                            onClick={() => handleDeleteSprint(dateIndex, themeIndex, sprintIndex)}
                            className="text-red-500 hover:underline"
                          >
                            Delete Sprint
                          </button>
                        </div>
                      </div>
                    ))}

                      <div className="flex justify-end">
                          <button
                            onClick={() => handleAddSprint(dateIndex, themeIndex)}
                            className="bg-blue-500 text-white py-2 px-4 rounded mt-4"
                          >
                            Add Sprint
                          </button>
                      </div>
                  </div>
                ))}
              </div>
            ))}
              <div className="flex justify-center">
                  <button
                  onClick={handleSave}
                  className={`bg-green-500 text-white py-2 px-4 rounded mt-4 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Workout"}
                </button>
              </div>
          
          </div>


      </div>
    </div>
  );
};
